// bm-design-system: progress primitive
//
// A hairline track with a gold fill. Purely presentational: the caller passes
// the value (e.g. bottles selected) and the target (e.g. the 100-bottle minimum).
// SSR-safe: no browser globals.
import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/lib/utils";

const progressTrackVariants = cva("relative w-full overflow-hidden bg-hairline", {
  variants: {
    size: {
      sm: "h-px",
      md: "h-[3px]",
    },
  },
  defaultVariants: { size: "md" },
});

export interface ProgressProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, "children">,
    VariantProps<typeof progressTrackVariants> {
  /** Current value. Values above `max` render as a full bar. */
  value: number;
  /** Target value; default 100. */
  max?: number;
  /** Accessible name, e.g. "Bottles towards the 100-bottle minimum". */
  label: string;
  /** Optional human reading, e.g. "62 of 100 bottles". */
  valueText?: string;
}

const Progress = React.forwardRef<HTMLDivElement, ProgressProps>(
  ({ value, max = 100, label, valueText, size, className, ...props }, ref) => {
    const safeMax = max > 0 ? max : 100;
    const clamped = Math.min(Math.max(value, 0), safeMax);
    const percent = (clamped / safeMax) * 100;

    return (
      <div
        ref={ref}
        role="progressbar"
        aria-label={label}
        aria-valuemin={0}
        aria-valuemax={safeMax}
        aria-valuenow={clamped}
        aria-valuetext={valueText}
        className={cn(progressTrackVariants({ size }), className)}
        {...props}
      >
        <div
          className="h-full bg-accent transition-[width] duration-500 ease-out"
          style={{ width: `${percent}%` }}
        />
      </div>
    );
  },
);
Progress.displayName = "Progress";

export { Progress, progressTrackVariants };
